import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "@/lib/navigation";
import {
  ApiError,
  authApi,
  isTwoFactorRequired,
  userApi,
  type CurrentUser,
  type TwoFactorRequired,
} from "@/lib/api";

/**
 * Auth context for the agent-portal.
 *
 * Backed by agent-portal-api + WorkOS (replaces the previous Supabase auth).
 * Exposes a slightly shape-compatible API to ease the transition — legacy call
 * sites that read `user`, `loading`, `signOut` keep working unchanged.
 */
interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;

  /** Sign in with email + password (calls /api/auth/sign-in). Returns the user,
   *  or a 2FA challenge when the account has email 2FA enabled — in which case
   *  no session is set yet and the caller must collect a code and call
   *  verifyTwoFactor. */
  signIn: (
    email: string,
    password: string,
  ) => Promise<CurrentUser | TwoFactorRequired>;
  /** Complete a 2FA-gated sign-in with the emailed code. */
  verifyTwoFactor: (challengeId: string, code: string) => Promise<CurrentUser>;
  signOut: () => Promise<void>;

  /** Re-fetch the current user from /api/auth/me. */
  reload: () => Promise<void>;

  impersonate: (userId: string) => Promise<void>;
  endImpersonation: () => Promise<void>;

  // ─── Compatibility shims for legacy call sites ───
  /** Legacy alias — old code reads `session?.user`. We expose `{ user }` for spread compat. */
  session: { user: CurrentUser } | null;
  /** Profile === user (we no longer separate them). */
  profile: CurrentUser | null;
  /** True when an impersonation session is active. */
  isImpersonating: boolean;
  /** Legacy shape — old hooks read `useAuth().impersonation.isImpersonating` etc. */
  impersonation: {
    isImpersonating: boolean;
    impersonatedUser: CurrentUser["impersonating"] | null;
  };
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const load = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        setError(err instanceof Error ? err.message : "Failed to load session");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await authApi.signIn(email, password);
    // 2FA-enabled accounts get a challenge instead of a session — leave `user`
    // null and let the caller collect the code.
    if (isTwoFactorRequired(result)) {
      return result;
    }
    setUser(result);
    setError(null);
    return result;
  }, []);

  const verifyTwoFactor = useCallback(
    async (challengeId: string, code: string) => {
      const me = await authApi.verifyTwoFactor(challengeId, code);
      setUser(me);
      setError(null);
      return me;
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      const res = await authApi.logout();
      setUser(null);
      if (res?.logoutUrl) {
        window.location.href = res.logoutUrl;
      } else {
        router.push("/login");
      }
    } catch {
      setUser(null);
      router.push("/login");
    }
  }, [router]);

  const impersonate = useCallback(
    async (userId: string) => {
      await userApi.startImpersonation(userId);
      await load();
    },
    [load],
  );

  const endImpersonation = useCallback(async () => {
    await userApi.endImpersonation();
    await load();
  }, [load]);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    signIn,
    verifyTwoFactor,
    signOut,
    reload: load,
    impersonate,
    endImpersonation,
    session: user ? { user } : null,
    profile: user,
    isImpersonating: !!user?.impersonating,
    impersonation: {
      isImpersonating: !!user?.impersonating,
      impersonatedUser: user?.impersonating ?? null,
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
